import { IncomingMessage } from '@app/core/services/incoming-messages.service';

export const MAIL_DROP_FALLBACK_HEADER = 'X-eAssur-Mail-Drop: fallback';

export async function fileContainsMailDropFallback(file: File): Promise<boolean> {
  const snippet = await file.slice(0, Math.min(file.size, 8192)).text();
  return snippet.includes(MAIL_DROP_FALLBACK_HEADER);
}

const LEGACY_MAIL_DROP_BODY_FALLBACK = 'Email dragged from mail client — full body unavailable in browser';

export interface MailDropPayload {
  stringsByType: Record<string, string>;
  uriList: string;
  plainText: string;
  html: string;
  messageId: string;
  subject: string;
}

const MAIL_DROP_PLAIN_TYPES = ['text/plain', 'Text', 'text', 'public.utf8-plain-text'];
const MAIL_DROP_HTML_TYPES = ['text/html', 'public.html', 'text/x-moz-html'];
const MAIL_DROP_URI_TYPES = ['text/uri-list', 'text/x-moz-url', 'public.url'];

export function snapshotMailDropStrings(dataTransfer: DataTransfer): Record<string, string> {
  const strings: Record<string, string> = {};

  for (const type of Array.from(dataTransfer.types || [])) {
    if (type === 'Files') {
      continue;
    }

    try {
      const data = dataTransfer.getData(type);
      if (data) {
        strings[type] = data;
      }
    } catch {
      // Some browsers reject unknown clipboard types.
    }
  }

  return strings;
}

export function startMailDropStringItemReads(
  items: DataTransferItemList | null | undefined,
): Promise<Record<string, string>> {
  if (!items) {
    return Promise.resolve({});
  }

  const reads = Array.from(items)
    .filter((item) => item.kind === 'string')
    .map((item) => new Promise<{ type: string; data: string }>((resolve) => {
      item.getAsString((data) => resolve({ type: item.type, data: data || '' }));
    }));

  if (reads.length === 0) {
    return Promise.resolve({});
  }

  return Promise.all(reads).then((results) => {
    const strings: Record<string, string> = {};
    for (const { type, data } of results) {
      if (!data) {
        continue;
      }

      if (!strings[type] || data.length > strings[type].length) {
        strings[type] = data;
      }
    }

    return strings;
  });
}

export function mergeMailDropStringMaps(...maps: Array<Record<string, string>>): Record<string, string> {
  const merged: Record<string, string> = {};

  for (const map of maps) {
    for (const [type, data] of Object.entries(map)) {
      if (!data) {
        continue;
      }

      if (!merged[type] || data.length > merged[type].length) {
        merged[type] = data;
      }
    }
  }

  return merged;
}

export function resolveMailDropPayload(stringsByType: Record<string, string>): MailDropPayload {
  const uriList = pickMailDropString(stringsByType, MAIL_DROP_URI_TYPES);
  const plainText = pickLongestPlainMailDropString(stringsByType);
  const html = pickMailDropString(stringsByType, MAIL_DROP_HTML_TYPES)
    || findHtmlLikeMailDropString(stringsByType);
  const messageUri = uriList
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line.toLowerCase().startsWith('message:'));

  return {
    stringsByType,
    uriList,
    plainText,
    html,
    messageId: extractMessageIdFromMailUri(messageUri || ''),
    subject: extractMailSubjectFromPlainText(plainText),
  };
}

export function mailDropPayloadWouldUseBodyFallback(payload: MailDropPayload): boolean {
  if (!hasMailDropSignals(payload)) {
    return false;
  }

  return buildEmlFromMailDropPayload(payload, '').includes(MAIL_DROP_FALLBACK_HEADER);
}

export function buildFilesFromMailDropPayload(payload: MailDropPayload, bodyFallback: string): File[] {
  if (!hasMailDropSignals(payload)) {
    return [];
  }

  const fileName = buildMailDropFileName(payload.subject, payload.messageId);
  const emlContent = buildEmlFromMailDropPayload(payload, bodyFallback);

  if (!emlContent.trim()) {
    return [];
  }

  return [new File([emlContent], fileName, { type: 'message/rfc822' })];
}

export function buildEmlFromMailDropPayload(payload: MailDropPayload, bodyFallback: string): string {
  const lines: string[] = [];
  const messageIdHeader = payload.messageId
    ? (payload.messageId.startsWith('<') ? payload.messageId : `<${payload.messageId}>`)
    : '';
  const fallbackBody = bodyFallback.trim() || LEGACY_MAIL_DROP_BODY_FALLBACK;
  const bodyText = extractMailBodyFromPlainText(payload.plainText, payload.subject);
  const htmlBody = normalizeMailDropHtml(payload.html);
  const plainContent = bodyText || (htmlBody ? stripHtmlToText(htmlBody) : '');
  const useFallback = !plainContent.trim();

  if (messageIdHeader) {
    lines.push(`Message-ID: ${messageIdHeader}`);
  }
  if (payload.subject) {
    lines.push(`Subject: ${payload.subject}`);
  }
  lines.push(`Date: ${new Date().toUTCString()}`);
  lines.push('MIME-Version: 1.0');

  if (useFallback) {
    lines.push(MAIL_DROP_FALLBACK_HEADER);
    const boundary = `alt_${Date.now()}`;
    lines.push(`Content-Type: multipart/alternative; boundary="${boundary}"`);
    lines.push('');
    lines.push(`--${boundary}`);
    lines.push('Content-Type: text/plain; charset=UTF-8');
    lines.push('Content-Transfer-Encoding: 8bit');
    lines.push('');
    lines.push(fallbackBody);
    lines.push(`--${boundary}`);
    lines.push('Content-Type: text/html; charset=UTF-8');
    lines.push('Content-Transfer-Encoding: 8bit');
    lines.push('');
    lines.push(buildMailDropFallbackHtml(fallbackBody));
    lines.push(`--${boundary}--`);
    return lines.join('\r\n');
  }

  if (htmlBody) {
    const boundary = `alt_${Date.now()}`;
    lines.push(`Content-Type: multipart/alternative; boundary="${boundary}"`);
    lines.push('');
    lines.push(`--${boundary}`);
    lines.push('Content-Type: text/plain; charset=UTF-8');
    lines.push('Content-Transfer-Encoding: 8bit');
    lines.push('');
    lines.push(plainContent);
    lines.push(`--${boundary}`);
    lines.push('Content-Type: text/html; charset=UTF-8');
    lines.push('Content-Transfer-Encoding: 8bit');
    lines.push('');
    lines.push(htmlBody);
    lines.push(`--${boundary}--`);
  } else {
    lines.push('Content-Type: text/plain; charset=UTF-8');
    lines.push('Content-Transfer-Encoding: 8bit');
    lines.push('');
    lines.push(plainContent);
  }

  return lines.join('\r\n');
}

export function buildEmlFromIncomingMessage(message: IncomingMessage): string {
  const lines: string[] = [];
  const fromValue = buildMailboxAddressValue(message.fromAddress, message.fromName);
  const toValues = readIncomingAddressList(message, 'to');
  const ccValues = readIncomingAddressList(message, 'cc');
  const dateValue = message.sentAt || message.receivedAt || message.createdAt || new Date().toISOString();
  const textBody = (message.textBody || '').replace(/\r?\n/g, '\r\n');
  const hasHtmlBody = typeof message.htmlBody === 'string' && message.htmlBody.trim().length > 0;
  const htmlBody = hasHtmlBody ? message.htmlBody!.replace(/\r?\n/g, '\r\n') : '';
  const alternativeBoundary = `alt_${Date.now()}`;

  if (fromValue) {
    lines.push(`From: ${fromValue}`);
  }
  if (toValues.length > 0) {
    lines.push(`To: ${toValues.join(', ')}`);
  }
  if (ccValues.length > 0) {
    lines.push(`Cc: ${ccValues.join(', ')}`);
  }
  if (message.subject) {
    lines.push(`Subject: ${message.subject}`);
  }
  if (dateValue) {
    lines.push(`Date: ${new Date(dateValue).toUTCString()}`);
  }
  if (message.messageId) {
    lines.push(`Message-ID: ${message.messageId}`);
  }
  lines.push('MIME-Version: 1.0');
  lines.push(...buildAlternativeBodyPartLines(hasHtmlBody, textBody, htmlBody, alternativeBoundary));

  return lines.join('\r\n');
}

export function isEmlContentIncomplete(content: string, subject = ''): boolean {
  const normalized = content.replace(/\r\n/g, '\n');
  const body = extractEmlBodyPreview(normalized);
  if (!body.trim()) {
    return true;
  }

  if (normalized.includes(MAIL_DROP_FALLBACK_HEADER)) {
    return true;
  }

  if (body.includes(LEGACY_MAIL_DROP_BODY_FALLBACK)) {
    return true;
  }

  const normalizedSubject = subject.trim().toLowerCase();
  const normalizedBody = body.trim().toLowerCase();
  if (normalizedSubject && normalizedBody === normalizedSubject) {
    return true;
  }

  return body.trim().length < 24;
}

export function buildMessageIdLookupCandidates(messageId: string): string[] {
  const trimmed = messageId.trim();
  if (!trimmed) {
    return [];
  }

  const withoutBrackets = trimmed.replace(/^<|>$/g, '');
  const withBrackets = trimmed.startsWith('<') ? trimmed : `<${withoutBrackets}>`;

  return Array.from(new Set([trimmed, withoutBrackets, withBrackets].filter((value) => value.length > 0)));
}

export function buildMailDropFileName(subject: string, messageId: string): string {
  const subjectBase = sanitizeMailDropFileName(subject);
  const messageBase = sanitizeMailDropFileName(messageId.replace(/^<|>$/g, ''));
  const baseName = subjectBase || messageBase || 'email';
  return baseName.toLowerCase().endsWith('.eml') ? baseName : `${baseName}.eml`;
}

function hasMailDropSignals(payload: MailDropPayload): boolean {
  return !!payload.messageId
    || !!payload.subject
    || !!payload.plainText.trim()
    || !!payload.html.trim()
    || !!payload.uriList.trim();
}

function pickMailDropString(stringsByType: Record<string, string>, preferredTypes: string[]): string {
  for (const type of preferredTypes) {
    const value = stringsByType[type];
    if (value) {
      return value;
    }
  }

  return '';
}

function pickLongestPlainMailDropString(stringsByType: Record<string, string>): string {
  let longest = pickMailDropString(stringsByType, MAIL_DROP_PLAIN_TYPES);

  for (const [type, value] of Object.entries(stringsByType)) {
    if (!value || MAIL_DROP_URI_TYPES.includes(type) || MAIL_DROP_HTML_TYPES.includes(type)) {
      continue;
    }

    const normalizedType = type.toLowerCase();
    if (normalizedType.includes('html') || normalizedType.includes('url')) {
      continue;
    }

    if (value.trim().toLowerCase().startsWith('message:')) {
      continue;
    }

    if (value.length > longest.length) {
      longest = value;
    }
  }

  return longest;
}

function findHtmlLikeMailDropString(stringsByType: Record<string, string>): string {
  for (const [type, value] of Object.entries(stringsByType)) {
    if (!value) {
      continue;
    }

    const normalizedType = type.toLowerCase();
    if (normalizedType.includes('html') || /<(?:html|body|div|p|table|span)\b/i.test(value)) {
      if (value.length > 0) {
        return value;
      }
    }
  }

  return '';
}

function extractMessageIdFromMailUri(messageUri: string): string {
  if (!messageUri) {
    return '';
  }

  const raw = messageUri.replace(/^message:/i, '').trim();
  if (!raw) {
    return '';
  }

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function extractMailSubjectFromPlainText(plainText: string): string {
  for (const line of plainText.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }

    if (trimmed.toLowerCase().startsWith('message:')
      || trimmed.includes('://')
      || trimmed.startsWith('%3C')) {
      continue;
    }

    return trimmed;
  }

  return '';
}

function extractMailBodyFromPlainText(plainText: string, subject: string): string {
  const lines = plainText.split(/\r?\n/);
  const bodyLines: string[] = [];
  let skippedSubject = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (skippedSubject && bodyLines.length > 0) {
        bodyLines.push('');
      }
      continue;
    }

    if (trimmed.toLowerCase().startsWith('message:') || trimmed.includes('://')) {
      continue;
    }

    if (!skippedSubject && subject && trimmed === subject) {
      skippedSubject = true;
      continue;
    }

    skippedSubject = true;
    bodyLines.push(line);
  }

  return bodyLines.join('\n').trim();
}

function normalizeMailDropHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) {
    return '';
  }

  if (/<(?:html|body|div|p|table|span)\b/i.test(trimmed)) {
    return trimmed;
  }

  return `<html><body>${escapeHtml(trimmed)}</body></html>`;
}

function extractEmlBodyPreview(content: string): string {
  const sections = content.split(/\n\n/);
  if (sections.length < 2) {
    return '';
  }

  const body = sections.slice(1).join('\n\n');
  const htmlMatch = body.match(/Content-Type:\s*text\/html[^\n]*\r?\n(?:[^\n]*\r?\n)*\r?\n([\s\S]*?)(?:\r?\n--|$)/i);
  if (htmlMatch?.[1]?.trim()) {
    return stripHtmlToText(htmlMatch[1]);
  }

  const plainMatch = body.match(/Content-Type:\s*text\/plain[^\n]*\r?\n(?:[^\n]*\r?\n)*\r?\n([\s\S]*?)(?:\r?\n--|$)/i);
  if (plainMatch?.[1]?.trim()) {
    return plainMatch[1].trim();
  }

  return body.trim();
}

function stripHtmlToText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildMailDropFallbackHtml(fallbackBody: string): string {
  return `<html><body><p>${escapeHtml(fallbackBody)}</p></body></html>`;
}

function sanitizeMailDropFileName(value: string): string {
  return value.replace(/[<>:"/\\|?*\u0000-\u001F]/g, '').trim().slice(0, 120);
}

function buildMailboxAddressValue(address?: string | null, name?: string | null): string {
  const normalizedAddress = (address || '').trim();
  const normalizedName = (name || '').trim();

  if (normalizedName && normalizedAddress) {
    return `"${normalizedName.replace(/"/g, '\\"')}" <${normalizedAddress}>`;
  }

  return normalizedAddress;
}

function readIncomingAddressList(message: IncomingMessage, field: 'to' | 'cc'): string[] {
  const addresses = message.jsonPayload?.addresses?.[field];
  if (!Array.isArray(addresses)) {
    return [];
  }

  return addresses
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

function buildAlternativeBodyPartLines(
  hasHtmlBody: boolean,
  textBody: string,
  htmlBody: string,
  alternativeBoundary: string,
): string[] {
  if (hasHtmlBody && textBody) {
    return [
      `Content-Type: multipart/alternative; boundary="${alternativeBoundary}"`,
      '',
      `--${alternativeBoundary}`,
      'Content-Type: text/plain; charset=UTF-8',
      'Content-Transfer-Encoding: 8bit',
      '',
      textBody,
      `--${alternativeBoundary}`,
      'Content-Type: text/html; charset=UTF-8',
      'Content-Transfer-Encoding: 8bit',
      '',
      htmlBody,
      `--${alternativeBoundary}--`,
    ];
  }

  if (hasHtmlBody) {
    return [
      'Content-Type: text/html; charset=UTF-8',
      'Content-Transfer-Encoding: 8bit',
      '',
      htmlBody,
    ];
  }

  return [
    'Content-Type: text/plain; charset=UTF-8',
    'Content-Transfer-Encoding: 8bit',
    '',
    textBody,
  ];
}
